/**
 * Errors signalling a tool harness misconfiguration or unrecoverable internal
 * state. These are NOT the LLM's fault and cannot be fixed by retrying.
 *
 * Recovery strategy: report to the user; do not retry.
 */

import { type ErrorRecoveryStrategy, ToolExecutionError } from './base.ts';
import { toolError } from './registry.ts';

@toolError('EnvironmentError')
export abstract class EnvironmentError extends ToolExecutionError {
  readonly recovery: ErrorRecoveryStrategy = 'fatal';

  /**
   * Default fatal-misconfiguration guidance. Subclasses with richer recovery
   * advice (e.g. {@link UpstreamNotRegisteredError}, {@link UnexpectedError})
   * should override.
   */
  override suggestions(): readonly string[] {
    return ['This is a tool harness misconfiguration. Report to the user; the LLM cannot recover from this.'];
  }
}

/**
 * A required runtime client (CDF SDK, query client, etc.) was not provided
 * to the tool's pipeline context.
 */
@toolError('ClientNotAvailableError')
export class ClientNotAvailableError extends EnvironmentError {
  constructor(readonly clientName: string) {
    super(`Required client "${clientName}" is not available in the pipeline context.`);
  }
}

/** The configured set of data-modeling views was not provided to the pipeline context. */
@toolError('ViewsNotAvailableError')
export class ViewsNotAvailableError extends EnvironmentError {
  constructor() {
    super('No data-modeling views are configured for this workspace.');
  }
}

/**
 * A pipeline operator step was invoked without an upstream input dataset.
 * Programming error — operator steps declare `consumesInput: true` and the
 * engine should have caught this before reaching the step.
 */
@toolError('MissingPipelineInputError')
export class MissingPipelineInputError extends EnvironmentError {
  constructor(readonly stepId: string) {
    super(`Pipeline step "${stepId}" requires an upstream input dataset but received undefined.`);
  }
}
/**
 * Thrown by `assertContextFields` when a retrieval step's required
 * `PipelineContext` field is missing at execute time. Carries the step id
 * (for diagnostics) and the offending field name.
 *
 * Sits in the `EnvironmentError` branch so the missing-field signal
 * propagates through the tool boundary as a structured `ToolErrorResponse`
 * (recovery: `'fatal'`) rather than as a plain `Error` that the boundary
 * would wrap in `UnexpectedError`.
 *
 * `field` is typed as `string` rather than `keyof PipelineContext` to keep
 * `lib/tool/errors/` free of pipeline-layer imports — the call site in
 * `assertContextFields` enforces the keyof constraint statically.
 */
@toolError('MissingPipelineContextFieldError')
export class MissingPipelineContextFieldError extends EnvironmentError {
  constructor(
    readonly stepId: string,
    readonly field: string,
  ) {
    super(`Pipeline step "${stepId}" requires ctx.${field}, but it was not provided.`);
  }
}

/**
 * A producer tool call was found in chat history (so its result was cached at
 * one point) but the cache no longer holds it. Indicates the dataset cache
 * was evicted, the producer tool failed silently, or the cache key changed.
 */
@toolError('UpstreamNotRegisteredError')
export class UpstreamNotRegisteredError extends EnvironmentError {
  constructor(
    readonly producerToolCallId: string,
    readonly outputDataset: string,
  ) {
    super(
      `Upstream dataset "${outputDataset}" (produced by tool call ${producerToolCallId}) is not registered in the dataset cache.`,
    );
  }

  override suggestions(): readonly string[] {
    return [
      'Re-run the producer tool call to re-populate the cache.',
      'This usually indicates a cache eviction or a producer that failed silently.',
    ];
  }
}

/**
 * Catch-all for unexpected JavaScript errors thrown from a tool's
 * `execute` that aren't a `ToolExecutionError` subclass.
 * Wrapped at the `<Tool>` execute boundary.
 */
@toolError('UnexpectedError')
export class UnexpectedError extends EnvironmentError {
  override suggestions(): readonly string[] {
    return [
      'This issue indicates an internal bug.',
      'Abort your current task and summarize the issue to the user in plain English; do not use technical jargon or error codes.',
    ];
  }
}
